import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { USER_BADGE_COLORS } from '../userBadgeColors';
import {
  terrainCoverageCount,
  fromPayload,
  unresolvedNodes,
  coverage,
  type PerimeterNode,
  type PerimeterTree,
} from './perimeterTree.logic';

export interface PerimeterSummaryProps {
  nodes: PerimeterNode[];
  tree: PerimeterTree;
  onEdit: () => void;
  /** Terrains still loading — the tree can't resolve nodes yet; disable editing. */
  loading?: boolean;
  /** GET /terrains failed — surfaced so an empty tree isn't read as "no perimeter". */
  error?: boolean;
  /** The form requires a perimeter but none is selected (submit-time validation). */
  validationError?: boolean;
}

/**
 * Perimeter summary of the user form's Authorisations block (HOMCO-1249): one chip
 * per retained node (plus raw chips for nodes the tree can't resolve, so a stored
 * perimeter is never shown as empty), the « N sélectionnés · M couverts » line, and
 * the « Modifier la sélection » button that opens the drawer.
 */
export function PerimeterSummary({ nodes, tree, onEdit, loading, error, validationError }: PerimeterSummaryProps) {
  const { t } = useTranslation('users');
  const roots = fromPayload(nodes, tree);
  const unknown = unresolvedNodes(nodes, tree);
  const coveredCount = terrainCoverageCount(roots, tree) + unknown.length;

  const MAX_VISIBLE = 3;

  const nodeLabel = (id: string) => {
    const node = tree.byId[id];
    return node.type === 'all' ? t('perimeter.all') : node.label;
  };
  const rawLabel = (node: PerimeterNode) =>
    node.type === 'all' ? t('perimeter.all') : (node.code ?? '—');

  const coveredSet = coverage(roots, tree);
  const collapsedChips = (): string[] => {
    const result: string[] = [];
    const consumed = new Set<string>();
    for (const paysNode of tree.root.children) {
      if (paysNode.type !== 'pays' || paysNode.children.length === 0) continue;
      const allCovered = paysNode.children.every(child => coveredSet.has(child.id));
      if (allCovered && paysNode.children.some(child => roots.includes(child.id))) {
        paysNode.children.forEach(child => consumed.add(child.id));
        result.push(paysNode.id);
      }
    }
    for (const id of roots) {
      if (!consumed.has(id)) result.push(id);
    }
    return result;
  };
  const displayRoots = collapsedChips();
  const total = displayRoots.length + unknown.length;

  const allChips: { key: string; label: string; isAll: boolean; isUnknown: boolean }[] = [
    ...displayRoots.map(id => ({
      key: id,
      label: nodeLabel(id),
      isAll: tree.byId[id].type === 'all',
      isUnknown: false,
    })),
    ...unknown.map((node, i) => ({
      key: `raw-${node.type}-${node.code ?? i}`,
      label: rawLabel(node),
      isAll: false,
      isUnknown: true,
    })),
  ];
  const visibleChips = allChips.slice(0, MAX_VISIBLE);
  const hiddenChips = allChips.slice(MAX_VISIBLE);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap',
        minHeight: 38,
        p: '9px 11px',
        border: validationError ? '1px solid #d32f2f' : '1px solid #D8DEE5',
        borderRadius: '6px',
        bgcolor: '#fff',
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '6px', alignItems: 'center' }}>
        {nodes.length === 0 ? (
          <Chip
            size="small"
            variant="outlined"
            label={t('perimeterSummary.empty')}
            sx={{ color: USER_BADGE_COLORS.default.color }}
          />
        ) : (
          <>
            {visibleChips.map(({ key, label, isAll, isUnknown }) => {
              const colors = USER_BADGE_COLORS[isAll ? 'allCountries' : 'default'];
              return (
                <Chip
                  key={key}
                  size="small"
                  variant={isUnknown ? 'outlined' : 'filled'}
                  label={label}
                  sx={{ color: colors.color, ...(!isUnknown && { bgcolor: colors.bgcolor }) }}
                />
              );
            })}
            {hiddenChips.length > 0 && (
              <Tooltip
                title={hiddenChips.map(c => c.label).join(', ')}
                placement="top"
              >
                <Chip
                  size="small"
                  label={`+${hiddenChips.length}`}
                  sx={{
                    color: USER_BADGE_COLORS.default.color,
                    bgcolor: USER_BADGE_COLORS.default.bgcolor,
                    cursor: 'default',
                  }}
                />
              </Tooltip>
            )}
          </>
        )}
      </Box>
      {/* #64748B ≈ 4.6:1 on white — AA for this 11px text (incl. the load error);
          #94A3B8 was ≈ 2.6:1, below the 4.5:1 minimum for small text. */}
      <Typography sx={{ color: '#64748B', fontSize: '11px', lineHeight: 1.5 }}>
        {error
          ? t('perimeterSummary.loadError')
          : t('perimeterSummary.summary', { selected: total, covered: coveredCount })}
      </Typography>
      <Box sx={{ flex: 1 }} />
      <Button
        id="uf-perimeter"
        variant="text"
        size="small"
        onClick={onEdit}
        disabled={loading || error}
        sx={{
          textTransform: 'none',
          color: '#3D6F9C',
          fontWeight: 600,
          fontSize: '11.5px',
          minWidth: 0,
          px: '8px',
          flexShrink: 0,
        }}
      >
        {t('perimeterSummary.edit')}
      </Button>
    </Box>
  );
}
